import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Inbox, Building2, FileText, Settings, BarChart3,
  ChevronLeft, ChevronRight, LogOut, Zap, Menu, X, Database, FlaskConical, CalendarClock
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';

const navItems = [
  { path: '/dashboard', label: 'MONITOR', icon: LayoutDashboard },
  { path: '/responses', label: 'RESPONSE LOG', icon: Inbox },
  { path: '/dealerships', label: 'DEALERSHIPS', icon: Building2 },
  { path: '/deactivated', label: 'DEACTIVATED', icon: Building2, sub: true },
  { path: '/scheduling', label: 'CONVERSATIONS', icon: CalendarClock },
  { path: '/templates', label: 'TEMPLATES', icon: FileText },
  { path: '/reporting', label: 'REPORTING', icon: BarChart3 },
  { path: '/repository', label: 'REPOSITORY', icon: Database },
  { path: '/vinsolutions', label: 'VINSOLUTIONS', icon: Zap },
  { path: '/samples', label: 'SAMPLES', icon: FlaskConical },
  { path: '/settings', label: 'SETTINGS', icon: Settings },
];

export default function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-50 top-0 left-0 h-screen bg-[#0A0A0A] border-r border-[#262626] transition-all duration-300 flex flex-col
          ${collapsed ? 'w-20' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-[#262626]">
          <Zap className="w-6 h-6 text-red-600 flex-shrink-0" />
          {!collapsed && (
            <span className="ml-3 font-heading text-lg font-bold tracking-wider text-white uppercase">
              My ETA
            </span>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => {
                  setMobileOpen(false);
                  if (item.path === '/responses') {
                    sessionStorage.removeItem('responseLogFilters');
                    sessionStorage.removeItem('responseLogPage');
                    sessionStorage.removeItem('responseLogScroll');
                  }
                }}
                data-testid={`nav-${item.path.replace('/', '')}`}
                className={`flex items-center gap-3 ${item.sub ? 'pl-6' : 'px-3'} ${!item.sub ? 'px-3' : 'pr-3'} py-2.5 rounded-md transition-all duration-200 group
                  ${isActive
                    ? 'bg-red-600/10 text-red-500 border-l-2 border-red-600'
                    : 'text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                  }
                `}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${item.sub ? 'w-4 h-4' : ''} ${isActive ? 'text-red-500' : 'text-slate-500 group-hover:text-white'}`} strokeWidth={1.5} />
                {!collapsed && (
                  <span className={`font-bold tracking-widest ${item.sub ? 'text-[9px]' : 'text-xs'}`}>{item.label}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle (desktop) */}
        <div className="hidden lg:block border-t border-[#262626] p-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 text-slate-500 hover:text-white rounded-md hover:bg-white/5 transition-colors"
            data-testid="collapse-sidebar-btn"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* User section */}
        <div className="border-t border-[#262626] p-4">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-red-600/20 text-red-400 text-xs font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            )}
            {!collapsed && (
              <button
                onClick={logout}
                className="text-slate-500 hover:text-red-400 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden h-14 bg-[#0A0A0A] border-b border-[#262626] flex items-center px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex items-center ml-3">
            <Zap className="w-5 h-5 text-red-600" />
            <span className="ml-2 font-heading text-sm font-bold tracking-wider text-white uppercase">
              My ETA
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
